import { useCallback, useEffect, useState } from "react";
import { Save } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import { DEFAULT_SYSLOG_SETTINGS, type GenericResponse, type SyslogSettings } from "@/lib/types";

function parseIps(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function SyslogPage() {
  const [cfg, setCfg] = useState<SyslogSettings>(DEFAULT_SYSLOG_SETTINGS);
  const [ips, setIps] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<SyslogSettings>("/api/settings/syslog");
      setCfg(data);
      setIps((data.allowed_ips ?? []).join("\n"));
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const resp = await api.post<GenericResponse>("/api/settings/syslog", {
        ...cfg,
        allowed_ips: parseIps(ips),
      });
      if (resp.success) {
        setFeedback({ kind: "success", text: resp.message });
        await reload();
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Speichern fehlgeschlagen") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      <Card
        title="Syslog"
        description={t("Nimmt Syslog-Nachrichten (RFC 5424 und RFC 3164) über UDP und TCP an: Switches, Firewalls, Gateways, USVs. Änderungen greifen sofort, ohne Neustart.")}
      >
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : (
          <form onSubmit={onSave} className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex cursor-pointer items-center gap-2 md:col-span-2">
              <input
                type="checkbox"
                checked={cfg.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                className="h-4 w-4 cursor-pointer accent-primary"
              />
              <span className="text-sm">{t("Syslog-Ingest aktiv")}</span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Port (UDP + TCP)")}
              </span>
              <input
                type="number"
                min={1}
                max={65535}
                value={cfg.port}
                onChange={(e) => setCfg({ ...cfg, port: Number(e.target.value) })}
                className="h-9 rounded-md border border-border bg-background px-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <span className="text-[11px] text-muted-foreground">
                {t("Container-intern. Nach außen mappt docker-compose (Standard: 49514 → 5514).")}
              </span>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Client-ID")}
              </span>
              <input
                type="text"
                value={cfg.client_id}
                onChange={(e) => setCfg({ ...cfg, client_id: e.target.value })}
                placeholder="syslog"
                autoComplete="off"
                className="h-9 rounded-md border border-border bg-background px-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <span className="text-[11px] text-muted-foreground">
                {t("Einträge werden unter dieser ID gespeichert")}
              </span>
            </label>

            <label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Erlaubte Absender-IPs")}
              </span>
              <textarea
                value={ips}
                onChange={(e) => setIps(e.target.value)}
                rows={3}
                placeholder={"10.0.0.7\n10.0.0.8"}
                className="rounded-md border border-border bg-background px-3 py-2 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <span className="text-[11px] text-muted-foreground">
                {t("Eine IP pro Zeile. Leer = alle Absender erlaubt. Syslog hat keine Authentifizierung — den Port nur im vertrauenswürdigen Netz freigeben.")}
              </span>
            </label>

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground disabled:opacity-60"
              >
                <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : t("Einstellungen speichern")}
              </button>
            </div>
          </form>
        )}
      </Card>

      <Card title={t("Feld-Zuordnung")}>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <th className="py-2 text-left font-medium">Syslog</th>
              <th className="py-2 text-left font-medium">Leif</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border">
              <td className="py-2 pr-3 font-mono">Severity 0–2 / 3 / 4 / 5–6 / 7</td>
              <td className="py-2">FATAL / ERROR / WARN / INFO / DEBUG</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 pr-3 font-mono">HOSTNAME</td>
              <td className="py-2">Host</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 pr-3 font-mono">APP-NAME / TAG</td>
              <td className="py-2">Service</td>
            </tr>
            <tr>
              <td className="py-2 pr-3 font-mono">Facility, ProcID, MsgID, Structured Data, Absender-IP</td>
              <td className="py-2">{t("Metadaten")}</td>
            </tr>
          </tbody>
        </table>
        <p className="mt-3 text-[11px] text-muted-foreground">
          {t("Schnelltest von einem Linux-Host:")}
        </p>
        <div className="mt-1 rounded-md bg-muted/60 px-3 py-2 font-mono text-xs">
          logger -n &lt;leif-server&gt; -P 49514 -d "Testnachricht von logger"
        </div>
      </Card>
    </div>
  );
}
